import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import { getUserProfileById } from '../services/firebase/user-profile';
import { Heading } from '../components/common/TextComponents';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import Button from '../components/common/Button';
import Container from '../components/common/Container';
import PageLayout from '../components/layout/PageLayout';
import UserProfileHeader from '../components/features/profile/UserProfileHeader';
import UserProfileContent from '../components/features/profile/UserProfileContent';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { SkeletonUserProfile } from '../components/common/LoadingSkeleton';
import { useErrorHandler } from '../services/errorHandling';
import { useTheme } from '../context/ThemeContext';

/**
 * Enhanced error state component for better user experience
 */
const EnhancedErrorState = ({ 
  error, 
  onRetry, 
  onNavigateBack, 
  canRetry = true,
  retryCount = 0,
  maxRetries = 3 
}) => {
  const { isDarkMode } = useTheme();
  const errorInfo = error?.errorInfo || {
    title: 'Unable to Load Profile',
    message: 'Something went wrong while loading this profile.',
    suggestion: 'Please try again or go back to the directory.',
    canRetry: true
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8 lg:p-12`}>
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="p-3 sm:p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
            <ExclamationTriangleIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-red-600 dark:text-red-400" />
          </div>
        </div>
        
        <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
          {errorInfo.title}
        </h3>
        
        <p className="text-gray-600 dark:text-gray-300 mb-2 text-sm sm:text-base leading-relaxed px-2">
          {errorInfo.message}
        </p>
        
        <p className="text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 text-xs sm:text-sm">
          {errorInfo.suggestion}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {errorInfo.canRetry && canRetry && onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-purple-600 hover:bg-purple-700 
                       text-white font-medium rounded-lg text-sm sm:text-base
                       hover:shadow-lg hover:-translate-y-0.5
                       transition-all duration-200 ease-out
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                       dark:focus:ring-offset-gray-800 min-h-[44px]"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
            </button>
          )}
          
          {onNavigateBack && (
            <button
              onClick={onNavigateBack}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gray-600 hover:bg-gray-700 
                       text-white font-medium rounded-lg text-sm sm:text-base
                       hover:shadow-lg hover:-translate-y-0.5
                       transition-all duration-200 ease-out
                       focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 
                       dark:focus:ring-offset-gray-800 min-h-[44px]"
            >
                              <span className="hidden sm:inline">Back to Members Directory</span>
                <span className="sm:hidden">Back to Members</span>
            </button>
          )}
          
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-amber-600 hover:bg-amber-700 
                     text-white font-medium rounded-lg text-sm sm:text-base
                     hover:shadow-lg hover:-translate-y-0.5
                     transition-all duration-200 ease-out
                     focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 
                     dark:focus:ring-offset-gray-800 min-h-[44px]"
          >
            <svg 
              className="w-4 h-4 mr-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
            <span className="hidden sm:inline">Refresh Page</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
        
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
              Error Details (Development)
            </summary>
            <pre className="mt-2 text-xs text-red-600 dark:text-red-400 bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto max-h-32">
              {error.message || error.toString()}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * UserProfilePage component for displaying other users' profiles
 * Accessible via /people/:userId route
 * Shows public profile information in a responsive layout
 */
const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const { isDarkMode } = useTheme();
  const { handleErrorWithRecovery, createRetryHandler } = useErrorHandler('profile');
  
  // Component state
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryHandler] = useState(() => createRetryHandler(3, 1000, 'exponential'));

  // Fetch user profile data with enhanced error handling
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        const validationError = new Error('Invalid user ID');
        validationError.code = 'invalid-argument';
        const enhancedError = await handleErrorWithRecovery(validationError, { userId });
        setError(enhancedError);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use retry handler for robust data fetching
        const result = await retryHandler.execute(async () => {
          return await getUserProfileById(userId, user?.uid);
        }, 'profile');
        
        if (result.success) {
          setProfileData(result.data);
        } else {
          // Create appropriate error based on result
          const serviceError = new Error(result.error || 'Failed to load user profile');
          if (result.error === 'User not found') {
            serviceError.code = 'not-found';
          } else if (result.error === 'User profile not available') {
            serviceError.code = 'permission-denied';
          }
          
          const enhancedError = await handleErrorWithRecovery(serviceError, { userId, result });
          setError(enhancedError);
        }
      } catch (err) {
        const enhancedError = await handleErrorWithRecovery(err, { 
          userId, 
          retryCount: retryHandler.retryCount,
          retryStats: retryHandler.getStats()
        });
        setError(enhancedError);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]); // Only userId should trigger a refetch



  // Handle retry for failed requests with enhanced logic
  const handleRetry = async () => {
    if (!retryHandler.canRetry()) {
      // Reset retry handler if max retries reached
      retryHandler.reset();
    }

    setError(null);
    setLoading(true);
    
    try {
      const result = await retryHandler.execute(async () => {
        return await getUserProfileById(userId, user?.uid);
      }, 'profile');
      
      if (result.success) {
        setProfileData(result.data);
      } else {
        const serviceError = new Error(result.error || 'Failed to load user profile');
        if (result.error === 'User not found') {
          serviceError.code = 'not-found';
        } else if (result.error === 'User profile not available') {
          serviceError.code = 'permission-denied';
        }
        
        const enhancedError = await handleErrorWithRecovery(serviceError, { 
          userId, 
          result, 
          isRetry: true,
          retryStats: retryHandler.getStats()
        });
        setError(enhancedError);
      }
    } catch (err) {
      const enhancedError = await handleErrorWithRecovery(err, { 
        userId, 
        retryCount: retryHandler.retryCount,
        isRetry: true,
        retryStats: retryHandler.getStats()
      });
      setError(enhancedError);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    // Navigate to landing page first to avoid race conditions with protected routes
    navigate('/');
    // Then perform the logout
    await logout();
  };

  // Redirect to login if not authenticated
  if (!user) {
    navigate('/');
    return null;
  }

  // Check if user is viewing their own profile
  const isViewingOwnProfile = user && userId === user.uid;



  // Get user's display name for breadcrumb
  const getUserDisplayName = () => {
    if (loading) return 'Loading...';
    if (error || !profileData) return 'User Profile';
    
    const { firstName, lastName } = profileData.basicInfo || {};
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'User Profile';
  };



  return (
    <ErrorBoundary 
onNavigateBack={() => navigate(-1)}
      maxRetries={3}
      autoRetry={true}
      resetOnPropsChange={true}
      resetKeys={[userId]}
      showDetails={process.env.NODE_ENV === 'development'}
    >
      <PageLayout showProfileMenu={true}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
          <Container>
            <div className="py-8 sm:py-10 lg:py-12">


              {loading ? (
                // Enhanced loading skeleton
                <SkeletonUserProfile />
              ) : error ? (
                // Enhanced error state
                <EnhancedErrorState 
                  error={error}
                  onRetry={handleRetry}
            onNavigateBack={() => navigate(-1)}
                  canRetry={retryHandler.canRetry()}
                  retryCount={retryHandler.retryCount}
                  maxRetries={3}
                />
              ) : (
                // Success state
                <>
                  {/* Public Profile Indicator - Shows when viewing own profile */}
                  {isViewingOwnProfile && (
                    <div 
                      className={`${isDarkMode ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-200'} 
                                 border rounded-lg p-4 mb-6`}
                      role="banner"
                      aria-label="Public profile view notification"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center">
                          <div 
                            className={`${isDarkMode ? 'bg-purple-800' : 'bg-purple-100'} 
                                       rounded-full p-2 mr-3`}
                            aria-hidden="true"
                          >
                            <svg 
                              className={`w-5 h-5 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <div>
                            <h3 
                              className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}
                              id="public-profile-heading"
                            >
                              Public Profile View
                            </h3>
                            <p 
                              className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}
                              id="public-profile-description"
                            >
                              This is how your profile appears to other users
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/profile')}
                          className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px]
                                     ${isDarkMode 
                                       ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                       : 'bg-purple-600 hover:bg-purple-700 text-white'
                                     } hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                          aria-label="Edit my profile"
                        >
                          <svg 
                            className="w-4 h-4 mr-2" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit Profile
                        </button>
                      </div>
                    </div>
                  )}



                  {/* Profile Header Component */}
                  <UserProfileHeader 
                    profileData={profileData} 
                    loading={loading} 
                  />

                  {/* Profile Content Component */}
                  <UserProfileContent 
                    profileData={profileData} 
                    loading={loading} 
                  />

                  {/* Logout Button - Only show for own profile */}
                  {isViewingOwnProfile && (
                    <div className="mt-12 flex justify-center">
                      <button
                        onClick={handleLogout}
                        className="px-6 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                      >
                        Log Out
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Container>
        </div>
      </PageLayout>
    </ErrorBoundary>
  );
};

export default UserProfilePage;